/**
 * Placeholder contact emails.
 *
 * The public driver-invite page collects a name and phone but no email, and
 * `contacts.email` is NOT NULL/UNIQUE — so a synthetic `driver-<digits>@invite.local`
 * address is stored instead. It is not deliverable and the driver has never seen
 * it, so it must never be shown as their address or offered as the identifier
 * they sign in with. They sign in with their phone number instead.
 *
 * Mirrors car-pool-backend/lib/contactIdentity.js; the two repos cannot share
 * code, so keep them in step.
 */

export const PLACEHOLDER_EMAIL_DOMAIN = 'invite.local';

export function isPlaceholderEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) return false;
  return normalized.endsWith(`@${PLACEHOLDER_EMAIL_DOMAIN}`);
}

/** The contact's real email, or null when there is none we can show or use. */
export function getUsableEmail(contact) {
  const email = String(contact?.email || '').trim();
  if (!email || isPlaceholderEmail(email)) return null;
  return email;
}

export function canEmailContact(contact) {
  return getUsableEmail(contact) !== null;
}

/** What this person signs in with: their email when real, otherwise their phone. */
export function getLoginIdentifier(contact) {
  const email = getUsableEmail(contact);
  if (email) return { identifier: email, kind: 'email' };

  const phone = String(contact?.phone || '').trim();
  if (phone) return { identifier: phone, kind: 'phone' };

  return { identifier: null, kind: 'none' };
}

/**
 * What to render where an email would go. `needsEmail` marks the contacts an
 * administrator has to chase for a real address.
 */
export function getContactEmailDisplay(contact) {
  const email = getUsableEmail(contact);
  if (email) return { email, needsEmail: false, label: email };

  return {
    email: null,
    needsEmail: true,
    label: contact?.phone
      ? 'No email on file — signs in with their phone number'
      : 'No email on file',
  };
}
